import { Link, usePage } from "@inertiajs/react"
import {
  Building2,
  ChevronDown,
  CreditCard,
  LayoutDashboard,
  LogOut,
  PanelsTopLeft,
  Search,
  Settings,
  Users,
} from "lucide-react"
import { useState } from "react"

import { NavMain } from "@/components/nav-main"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import {
  destroyPartnerUserSessionPath,
  partnerAccountsPath,
  partnerBillingPeriodsPath,
  partnerDashboardPath,
  partnerMarketingPath,
  partnerSettingsProfilePath,
} from "@/routes"
import type { NavItem } from "@/types"

import { PartnerSearchDialog } from "./partner-search-dialog"

const topNavItems: NavItem[] = [
  {
    title: "Search",
    href: "#",
    icon: Search,
  },
  {
    title: "Dashboard",
    href: partnerDashboardPath(),
    icon: LayoutDashboard,
    exactMatch: true,
  },
]

const manageNavItems: NavItem[] = [
  {
    title: "Accounts",
    href: partnerAccountsPath(),
    icon: Building2,
  },
  {
    title: "Billing",
    href: partnerBillingPeriodsPath(),
    icon: CreditCard,
  },
  {
    title: "Marketing",
    href: partnerMarketingPath(),
    icon: PanelsTopLeft,
  },
]

export function PartnerSidebar() {
  const { auth, partner } = usePage().props as unknown as {
    auth: {
      user: {
        name?: string
        email?: string
        avatar_initials?: string
        avatar_url?: string
      }
    }
    partner: { name?: string; logo_url?: string }
  }
  const [searchOpen, setSearchOpen] = useState(false)

  const partnerName = partner?.name ?? "Partner"
  const partnerInitials = partnerName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  const userName = auth?.user?.name ?? auth?.user?.email ?? ""
  const userInitials =
    auth?.user?.avatar_initials ??
    userName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase()

  return (
    <>
      <Sidebar collapsible="icon" variant="inset">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="h-8 w-8 rounded-md">
                      {partner?.logo_url && (
                        <AvatarImage
                          src={partner.logo_url}
                          alt={partnerName}
                          className="rounded-md object-cover"
                        />
                      )}
                      <AvatarFallback className="rounded-md bg-zinc-900 text-xs font-semibold text-white">
                        {partnerInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-1 flex-col text-left">
                      <span className="truncate text-sm leading-tight font-semibold">
                        {partnerName}
                      </span>
                    </div>
                    <ChevronDown className="text-muted-foreground ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-64"
                  align="start"
                  side="bottom"
                >
                  <DropdownMenuItem asChild>
                    <Link href={partnerSettingsProfilePath()}>
                      <Settings className="size-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href={partnerSettingsProfilePath()}>
                      <Users className="size-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="flex items-center gap-3 px-2 py-2">
                    <Avatar className="h-8 w-8 overflow-hidden rounded-full">
                      {auth?.user?.avatar_url && (
                        <AvatarImage
                          src={auth.user.avatar_url}
                          alt={userName}
                          className="object-cover"
                        />
                      )}
                      <AvatarFallback className="rounded-full bg-neutral-200 text-xs font-medium text-black dark:bg-neutral-700 dark:text-white">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm leading-tight font-medium">
                        {userName}
                      </span>
                      <span className="text-muted-foreground text-xs leading-tight">
                        {auth?.user?.email}
                      </span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="text-muted-foreground">
                    <Link
                      href={destroyPartnerUserSessionPath()}
                      method="delete"
                      as="button"
                      className="w-full"
                    >
                      <LogOut className="size-4" />
                      Sign out
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent className="pt-2">
          <NavMain
            items={topNavItems}
            onSearchClick={() => setSearchOpen(true)}
          />
          <NavMain items={manageNavItems} label="Manage" />
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              {/* Collapsing to the icon rail is desktop-only; on mobile the
                  sidebar is a sheet, dismissed by tapping outside it. */}
              <SidebarTrigger className="text-sidebar-foreground/70 hover:text-sidebar-foreground hidden cursor-pointer p-2 md:inline-flex" />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <PartnerSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  )
}
